// Tier-B capture sink (mobile WebKit): canvas.captureStream + the audio-tap track
// → MediaRecorder → MP4 blob. Same CaptureRecorder contract as Mp4Recorder.
// pushVideoFrame is a no-op — captureStream self-samples the canvas the compositor
// tick already draws, so there is no manual pump and no fps throttle here.
import type { CaptureRecorder } from "./recorder";

interface MediaRecorderLike {
  state: string;
  ondataavailable: ((e: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((e: unknown) => void) | null;
  start(timesliceMs?: number): void;
  stop(): void;
}
interface MediaRecorderCtorLike {
  new (stream: MediaStream, options?: { mimeType?: string; videoBitsPerSecond?: number; audioBitsPerSecond?: number }): MediaRecorderLike;
}
interface MediaStreamCtorLike { new (tracks: MediaStreamTrack[]): MediaStream }

export interface MediaRecorderSinkOptions {
  /** The compositor's offscreen canvas (CaptureController owns it). */
  canvas: HTMLCanvasElement;
  dims: { w: number; h: number };
  fps?: number;
  audioTrack?: MediaStreamTrack | null;
  /** From pickMp4Mime() — Tier-B never starts without a recordable MP4 mime. */
  mimeType: string;
  videoBitrate?: number;
  audioBitrate?: number;
  /** Max time to wait for the recorder's stop event before giving up (see STOP_TIMEOUT_MS). */
  stopTimeoutMs?: number;
  deps?: { MediaRecorder?: MediaRecorderCtorLike; MediaStream?: MediaStreamCtorLike };
}

// 1s timeslice: iOS historically misbehaves when data is only requested at stop;
// periodic chunks also keep memory incremental instead of one giant final buffer.
const TIMESLICE_MS = 1000;

// Some WebKit recorders can die (state flips to "inactive") without ever firing
// their stop event — awaiting stopPromise alone would then hang forever. Bound
// the wait so a dead recorder degrades to a truncated-but-delivered (or cleanly
// null) result instead of hanging the export flow.
const STOP_TIMEOUT_MS = 3000;

export class MediaRecorderSink implements CaptureRecorder {
  private chunks: Blob[] = [];
  private recorder: MediaRecorderLike | null = null;
  private stream: MediaStream | null = null;
  private stopPromise: Promise<void> | null = null;
  private started = false;
  private aborted = false;

  constructor(private opts: MediaRecorderSinkOptions) {}

  async start(): Promise<boolean> {
    const g = globalThis as { MediaRecorder?: MediaRecorderCtorLike; MediaStream?: MediaStreamCtorLike };
    const MR = this.opts.deps?.MediaRecorder ?? g.MediaRecorder;
    const MS = this.opts.deps?.MediaStream ?? g.MediaStream;
    const canvas = this.opts.canvas as HTMLCanvasElement & { captureStream?: (fps?: number) => MediaStream };
    if (!MR || !MS || typeof canvas.captureStream !== "function") return false;
    try {
      const video = canvas.captureStream(this.opts.fps ?? 30);
      const tracks: MediaStreamTrack[] = [...video.getVideoTracks()];
      if (this.opts.audioTrack) tracks.push(this.opts.audioTrack);
      const stream = new MS(tracks);
      this.stream = stream;
      // Bitrate hints (the platform encoder has the final word): 10 Mbps at the
      // 1080p tier (Tier-A parity), 6 Mbps at 720p, 128 kbps audio.
      const hd = Math.min(this.opts.dims.w, this.opts.dims.h) >= 1080;
      const recorder = new MR(stream, {
        mimeType: this.opts.mimeType,
        videoBitsPerSecond: this.opts.videoBitrate ?? (hd ? 10_000_000 : 6_000_000),
        audioBitsPerSecond: this.opts.audioBitrate ?? 128_000,
      });
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0 && !this.aborted) this.chunks.push(e.data);
      };
      recorder.onerror = (e) => {
        console.warn("[capture] MediaRecorder error", e);
        this.aborted = true;
      };
      this.stopPromise = new Promise<void>((resolve) => { recorder.onstop = () => resolve(); });
      recorder.start(TIMESLICE_MS); // iOS start() can throw — covered by this try
      this.recorder = recorder;
      this.started = true;
      return true;
    } catch (e) {
      console.warn("[capture] MediaRecorderSink start failed", e);
      this.cleanup();
      return false;
    }
  }

  /** No-op: captureStream samples the canvas directly. */
  pushVideoFrame(_source: CanvasImageSource, _timestampMicros: number): void {}

  /** Stop, await the recorder's own stop event, assemble the MP4 blob. Null if empty/aborted. */
  async finish(): Promise<Blob | null> {
    if (!this.started || !this.recorder) { this.cleanup(); return null; }
    try {
      if (this.recorder.state !== "inactive") this.recorder.stop();
      await Promise.race([
        this.stopPromise,
        new Promise<void>((resolve) => setTimeout(resolve, this.opts.stopTimeoutMs ?? STOP_TIMEOUT_MS)),
      ]);
      const blob = !this.aborted && this.chunks.length
        ? new Blob(this.chunks, { type: "video/mp4" })
        : null;
      this.cleanup(); // stops tracks in BOTH paths — parity with Mp4Recorder.cleanup (F2)
      return blob;
    } catch (e) {
      console.warn("[capture] MediaRecorderSink finish failed", e);
      this.cleanup();
      return null;
    }
  }

  /** Discard the in-progress capture (pause/seek/cancel mid-run). */
  abort(): void {
    this.aborted = true;
    try { if (this.recorder && this.recorder.state !== "inactive") this.recorder.stop(); } catch { /* ignore */ }
    this.cleanup();
  }

  private cleanup(): void {
    this.started = false;
    this.chunks = this.aborted ? [] : this.chunks;
    try { this.stream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    try { this.opts.audioTrack?.stop(); } catch { /* ignore */ }
    this.recorder = null;
    this.stream = null;
  }
}
